// Impersonation helper — swaps only the JWT so the admin can view any staff
// dashboard, then restores the admin JWT on exit. NO user objects are cached
// in localStorage (that used to blow past the quota). AuthContext.hydrates
// the user from /auth/me on every fresh boot.
//
// localStorage keys used:
//   shakthi_token       → active JWT (admin OR imp)
//   shakthi_admin_token → admin's original JWT (backup, only while impersonating)
//   shakthi_admin_name  → admin's display name (for the yellow banner)
//   shakthi_imp_exp     → epoch-ms when the imp token expires (for countdown)

import { safeSetItem } from "@/lib/authStorage";

const IMP_EVENT = "sakthi:impersonation-change";

export function isImpersonating() {
  try { return !!localStorage.getItem("shakthi_admin_token"); } catch { return false; }
}

export function getBackupAdminName() {
  try { return localStorage.getItem("shakthi_admin_name") || ""; } catch { return ""; }
}

export function getImpersonationExpiry() {
  try {
    const raw = localStorage.getItem("shakthi_imp_exp");
    return raw ? parseInt(raw, 10) : 0;
  } catch { return 0; }
}

/**
 * Save current admin JWT + name, then install the imp token.
 * @param {{token: string, expires_in_seconds?: number, admin?: {name?: string}}} payload
 */
export function beginImpersonation({ token, expires_in_seconds, admin }) {
  try {
    const adminToken = localStorage.getItem("shakthi_token");
    if (adminToken) safeSetItem("shakthi_admin_token", adminToken);
    if (admin?.name) safeSetItem("shakthi_admin_name", admin.name);
    safeSetItem("shakthi_token", token);
    const ttl = Number(expires_in_seconds || 1800);
    safeSetItem("shakthi_imp_exp", String(Date.now() + ttl * 1000));
  } catch { /* ignore quota */ }
  window.dispatchEvent(new Event(IMP_EVENT));
}

/** Restore admin JWT. Returns true if restored, false if there was nothing to restore. */
export function endImpersonation() {
  let restored = false;
  try {
    const adminToken = localStorage.getItem("shakthi_admin_token");
    if (adminToken) { safeSetItem("shakthi_token", adminToken); restored = true; }
    localStorage.removeItem("shakthi_admin_token");
    localStorage.removeItem("shakthi_admin_name");
    localStorage.removeItem("shakthi_imp_exp");
  } catch { /* ignore */ }
  window.dispatchEvent(new Event(IMP_EVENT));
  return restored;
}

export function onImpersonationChange(handler) {
  window.addEventListener(IMP_EVENT, handler);
  window.addEventListener("storage", handler);
  return () => {
    window.removeEventListener(IMP_EVENT, handler);
    window.removeEventListener("storage", handler);
  };
}
